import React, { useEffect, useState } from "react";
import api from "../services/api";
import { showToast } from "../helper/notifications";

const LocationHistoryModal = ({
  isOpen = true,
  onClose,
  endpoint,
  historyData: propHistoryData,
  isLoading: propIsLoading,
  onRestore,
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalHistory, setInternalHistory] = useState([]);
  const [locationTitle, setLocationTitle] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    if (!endpoint) return;

    const fetchHistory = async () => {
      setInternalLoading(true);
      try {
        const res = await api.get(endpoint);
        if (res.data?.success) {
          let list = [];
          if (Array.isArray(res.data.history)) {
            list = res.data.history;
          } else if (Array.isArray(res.data.data)) {
            list = res.data.data;
          } else if (Array.isArray(res.data.data?.history)) {
            list = res.data.data.history;
          }
          setInternalHistory(list);
          setLocationTitle(
            res.data.locationTitle ||
            res.data.title ||
            res.data.data?.title ||
            list[0]?.title ||
            list[0]?.location_name ||
            "Location"
          );
        }
      } catch (error) {
        showToast("error", "Failed to fetch version history");
      } finally {
        setInternalLoading(false);
      }
    };

    fetchHistory();
  }, [endpoint]);

  if (!isOpen && !endpoint) return null;

  const loading = endpoint ? internalLoading : propIsLoading;
  const rawHistory = endpoint ? internalHistory : propHistoryData;
  const historyList = Array.isArray(rawHistory) ? rawHistory : [];

  const getParsedPayload = (record) => {
    if (!record) return {};
    let data = record.raw_payload || record;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return { raw_content: data };
      }
    }
    return data;
  };

  const formatTableValue = (val) => {
    if (val === null || val === undefined) return <span className="text-secondary-light">—</span>;
    if (typeof val === "boolean") {
      return (
        <span className={`px-8 py-2 radius-4 text-xs fw-semibold ${val ? 'bg-success-focus text-success-main' : 'bg-neutral-200 text-neutral-600'}`}>
          {val ? "TRUE" : "FALSE"}
        </span>
      );
    }
    if (typeof val === "object") {
      return <pre className="font-monospace text-xs mb-0 bg-neutral-50 p-6 radius-4 border">{JSON.stringify(val, null, 2)}</pre>;
    }
    return String(val);
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content radius-12 p-16">
          
          {/* Header */}
          <div className="modal-header border-0 pb-12 d-flex justify-content-between align-items-center">
            <div>
              <h5 className="modal-title fw-bold">Audit History</h5>
              {locationTitle && (
                <p className="text-secondary-light text-xs mb-0">
                  Target: <strong>{locationTitle}</strong>
                </p>
              )}
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-40">
                <div className="spinner-border text-primary" role="status" />
                <p className="text-secondary-light text-xs mt-8">Loading history logs...</p>
              </div>
            ) : historyList.length === 0 ? (
              <div className="alert alert-info text-xs radius-8 mb-0">
                No history logs recorded for this location yet.
              </div>
            ) : (
              <div className="table-responsive radius-8 border">
                <table className="table align-middle text-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "35%" }}>Title / Name</th>
                      <th style={{ width: "35%" }}>Changed By</th>
                      <th style={{ width: "15%" }}>Updated At</th>
                      <th style={{ width: "15%" }} className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyList.map((item, index) => {
                      const titleDisplay = item.location_name || item.title || item.name || '—';
                      const versionNum = item.version_number || item.version || historyList.length - index;
                      const isLatest = index === 0;
                      const dateDisplay = item.updated_at || item.created_at || item.synced_at;
                      const userName = item.user_name || item.userName;
                      const userEmail = item.user_email || item.userEmail || item.email;
                      const userPicture = item.user_picture || item.picture;
                      const isExpanded = selectedRecord?.id === item.id;
                      const payloadEntries = isExpanded ? Object.entries(getParsedPayload(item)) : [];

                      return (
                        <React.Fragment key={item.id || index}>
                          <tr className={isExpanded ? "bg-primary-50 table-active" : ""}>
                            <td>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="badge bg-primary-50 text-primary-600 border border-primary-200 px-6 py-2 radius-4 text-xs">
                                  v{versionNum}
                                </span>
                                {isLatest && (
                                  <span className="badge bg-success-focus text-success-main px-6 py-2 radius-4 text-xs">
                                    Latest
                                  </span>
                                )}
                                <span className="fw-semibold text-primary-light">{titleDisplay}</span>
                              </div>
                              {(item.store_code || item.internal_id) && (
                                <div className="text-xs text-secondary-light mt-2">
                                  Code: {item.store_code || item.internal_id}
                                </div>
                              )}
                            </td>
                            <td>
                              {userName || userEmail ? (
                                <div className="d-flex align-items-center gap-2">
                                  <div 
                                    className="w-28-px h-28-px rounded-circle bg-primary-100 text-primary-600 d-flex align-items-center justify-content-center fw-bold text-xs flex-shrink-0 overflow-hidden"
                                  >
                                    {userPicture ? (
                                      <img src={userPicture} alt="User" className="w-100 h-100 object-fit-cover" />
                                    ) : (
                                      (userName?.[0] || userEmail?.[0] || 'U').toUpperCase()
                                    )}
                                  </div>
                                  <div>
                                    <div className="fw-semibold text-xs text-primary-light">
                                      {userName || userEmail?.split('@')[0] || "Authorized User"}
                                    </div>
                                    {userEmail && (
                                      <div className="text-secondary-light" style={{ fontSize: "11px", maxWidth: "160px" }}>
                                        {userEmail}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="badge bg-neutral-100 text-secondary-light px-8 py-4 radius-4 text-xs fw-normal">
                                  System / Sync
                                </span>
                              )}
                            </td>
                            <td className="text-secondary-light text-xs">
                              {dateDisplay ? new Date(dateDisplay).toLocaleString() : '—'}
                            </td>
                            <td className="text-end">
                              <div className="d-inline-flex align-items-center gap-1">
                                {onRestore && !isLatest && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning radius-8 text-xs py-4 px-8"
                                    onClick={() => onRestore(item)}
                                  >
                                    Restore
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className={`btn btn-sm radius-8 text-xs py-4 px-10 d-inline-flex align-items-center gap-1 ${
                                    isExpanded ? "btn-primary shadow-xs" : "btn-outline-secondary"
                                  }`}
                                  onClick={() => setSelectedRecord(isExpanded ? null : item)}
                                >
                                  {isExpanded ? (
                                    <>
                                      <i className="ri-arrow-up-s-line" /> Hide
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-arrow-down-s-line" /> Details
                                    </>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* INLINE EXPANDABLE PAYLOAD ACCORDION ROW */}
                          {isExpanded && (
                            <tr className="bg-neutral-50">
                              <td colSpan={4} className="p-0 border-0">
                                <div className="p-16 border-top border-bottom bg-neutral-50">
                                  <div className="d-flex justify-content-between align-items-center mb-12">
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="badge bg-primary-600 text-white text-xs px-8 py-3 radius-4">
                                        Snapshot Payload Details
                                      </span>
                                      <span className="text-xs text-secondary-light">
                                        Captured {payloadEntries.length} field(s)
                                      </span>
                                    </div>
                                    <button 
                                      type="button" 
                                      className="btn btn-xs btn-neutral-300 text-xs px-8 py-2 radius-4"
                                      onClick={() => setSelectedRecord(null)}
                                    >
                                      Close Details
                                    </button>
                                  </div>

                                  <div 
                                    className="table-responsive radius-8 border bg-white shadow-xs" 
                                    style={{ maxHeight: '280px', overflowY: 'auto' }}
                                  >
                                    <table className="table table-sm align-middle text-xs mb-0">
                                      <thead className="table-light sticky-top">
                                        <tr>
                                          <th style={{ width: '35%' }} className="text-secondary-light fw-bold ps-12">Field Name</th>
                                          <th style={{ width: '65%' }} className="text-secondary-light fw-bold pe-12">Field Value</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {payloadEntries.map(([key, value]) => (
                                          <tr key={key}>
                                            <td className="fw-semibold text-primary-light font-monospace ps-12">{key}</td>
                                            <td className="text-secondary-light pe-12">{formatTableValue(value)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer border-0 pt-0">
            <button type="button" className="btn btn-neutral-200 text-neutral-600 radius-8" onClick={onClose}>
              Close
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LocationHistoryModal;